import clsx from 'clsx';
import styles from './useStudyRouteCreating.module.scss';
import { FC, useCallback, useId, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, useFieldArray, useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { handleSubmitFormError } from 'src/application/error-handling/useErrorHandler';
import { useModal } from 'src/application/hooks/useModal';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { ValidationFormRules } from 'src/helpers/validation-helpers';
import { HookFormTextInput } from 'src/hookFormControls/hookFormTextInput';
import { navigateToRoute } from 'src/navigation/routes';
import {
  CheckPoint,
  CheckPointForm,
  CreateStudyRouteDto,
  FormActionObject,
  IFormDto,
  RouteProgressStateEnum,
  StepTimeLimitation,
} from 'src/services/api/api-client';
import { useGetFormsQuery } from 'src/services/api/api-client/FormsQuery';
import { getAdminStudiesQueryKey } from 'src/services/api/api-client/StudyQuery';
import { createStudyRoute, getStudyRouteById } from 'src/services/api/api-client/StudyRoutesClient';
import { AppButton } from 'uikit/buttons';
import { AppDropdownField, AppRadioGroupField } from 'uikit/fields';
import { DropdownOption } from 'uikit/inputs/dropdown/appDropdownInput';
import { enumToDropdownOptions } from 'uikit/inputs/dropdown/dropdown-helper';
import { ReactComponent as AddIcon } from 'assets/img/common/add.svg';
import { AppInputLabel } from 'uikit/wrappers';
import { HookFormCheckboxField } from 'src/hookFormControls/HookFormCheckboxGroupField';
import { AppStepper } from 'src/components/stepper/stepper.component';

type FormState = {
  scheme: CheckPoint[];
  toInitEvent: ('StepDone' | 'DateField' | undefined)[];
};

type StudyRouteCreatingModalProps = {
  visible: boolean;
  onHide: () => void;
  onSubmit: () => void;
  form: UseFormReturn<FormState>;
  forms: IFormDto[] | undefined;
  studyId: number;
};

export const useStudyRouteCreating = () => {
  const modal = useModal();
  const queryClient = useQueryClient();
  const [groupId, setGroupId] = useState<number>();
  const [studyId, setStudyId] = useState<number>();
  const { data: availableForms } = useGetFormsQuery({ studyId: studyId! }, { enabled: !!studyId });
  const form = useForm<FormState>({
    defaultValues: {},
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: true,
  });

  const submit = useCallback(
    async (values: FormState) => {
      if (!groupId) return;

      try {
        await createStudyRoute(
          new CreateStudyRouteDto({
            groupId: groupId,
            scheme: values.scheme,
          }),
        );
        modal.closeModal();
      } catch (e) {
        handleSubmitFormError(e, form.setError);
        return;
      }

      await queryClient.invalidateQueries(getAdminStudiesQueryKey());
    },
    [form.setError, groupId, modal, queryClient],
  );

  return {
    element: modal.visible && studyId && (
      <StudyRouteCreatingModal
        form={form}
        onHide={modal.closeModal}
        visible={modal.visible}
        onSubmit={form.handleSubmit(submit)}
        forms={availableForms}
        studyId={studyId}
      />
    ),
    start: async (sId: number, gId: number, routeId?: string | null) => {
      setGroupId(gId);
      setStudyId(sId);
      form.reset();

      if (routeId) {
        const route = await getStudyRouteById(routeId);
        form.setValue('scheme', route.schema);
        form.setValue(
          'toInitEvent',
          route.schema.map((x) =>
            !!x.limitation.startWhenStepWithNameDone
              ? 'StepDone'
              : !!x.limitation.startDateActionObject.dataKey
              ? 'DateField'
              : undefined,
          ),
        );
      }

      modal.openModal();
    },
  };
};

const StudyRouteCreatingModal: FC<StudyRouteCreatingModalProps> = ({
  form,
  onHide,
  onSubmit,
  visible,
  forms,
  studyId,
}) => {
  const formId = useId();

  const { t } = useScopedTranslation('StudyRouteCreating', 'dev');
  const { t: commonLocalization } = useTranslation();

  const scheme = form.watch('scheme');
  const checkpointArray = useFieldArray({
    control: form.control,
    name: 'scheme',
  });

  const availableFormOptions: DropdownOption[] = useMemo(() => {
    return (
      forms?.map((x) => ({
        key: x.id,
        text: x.type,
      })) ?? []
    );
  }, [forms]);

  const initStateOptions = useMemo(() => {
    return enumToDropdownOptions(RouteProgressStateEnum).filter((x) =>
      [RouteProgressStateEnum.None, RouteProgressStateEnum.Init, RouteProgressStateEnum.InProgress].includes(
        RouteProgressStateEnum[x.key],
      ),
    );
  }, []);

  const [currentStep, setCurrentStep] = useState(1);

  const stepNameOptions: DropdownOption<string>[] = useMemo(
    () =>
      scheme
        ?.map(
          (x) =>
            ({
              key: x.name,
              text: x.name,
            } satisfies DropdownOption<string>),
        )
        // @ts-ignore Exclude current step
        .toSpliced(currentStep - 1, 1),
    [currentStep, scheme],
  );

  const transferToInitEventOptions = useMemo(
    () =>
      [
        {
          key: 'StepDone',
          text: 'Step with name done',
        },
        {
          key: 'DateField',
          text: 'Start from date field',
        },
      ] satisfies DropdownOption<'StepDone' | 'DateField'>[],
    [],
  );

  const getExistedStepNames = useCallback(
    // @ts-ignore
    (index: number): string[] => scheme?.map((x) => x.name).toSpliced(index, 1),
    [scheme],
  );

  const nameValidationRule = useCallback(
    (index: number) => (value: any) => getExistedStepNames(index).some((x) => x === value) ? t('NameExists') : true,
    [getExistedStepNames, t],
  );

  const selectStepNameValidationRule = useCallback(
    (index: number) => (value: any) =>
      !getExistedStepNames(index).some((x) => x === value) ? t('StepNotExisted') : true,
    [getExistedStepNames, t],
  );

  return (
    <DialogModal
      visible={visible}
      onHide={onHide}
      title={t('Header')}
      bodyClassName={styles.modalBody}
      footer={{
        errors: !!form.formState.errors.root ? t('CommonError') : undefined,
        leftButton: {
          text: commonLocalization('Common_Cancel'),
          onClick: onHide,
          disabled: form.formState.isSubmitting,
        },
        rightButton: {
          text: commonLocalization('Common_Save'),
          type: 'submit',
          disabled: form.formState.isSubmitting,
          isLoading: form.formState.isSubmitting,
          form: formId,
          onClick: async () => {
            const isValid = await form.trigger('scheme');
            if (isValid) onSubmit();

            const errorField = form.formState.errors;
            const index = errorField.scheme?.findIndex?.((x) => !!x) ?? -1;
            if (index > -1) setCurrentStep(index + 1);
          },
        },
      }}
    >
      <div className={styles.form}>
        {!!checkpointArray.fields.length && (
          <AppStepper
            activeStep={currentStep}
            stepCount={scheme.length}
            variant={'tabs'}
            labels={scheme.map((x) => x.name)}
            onStepClick={setCurrentStep}
          />
        )}
        <div className={styles.stepContainer}>
          {checkpointArray.fields.map((step, index) => (
            <div
              key={step.id}
              className={clsx(styles.stepItem, { [styles.stepVisibility]: currentStep !== index + 1 })}
            >
              <HookFormTextInput
                control={form.control}
                name={`scheme.${index}.name`}
                labelProps={{ text: t('Name.FieldLabel') }}
                placeholder={t('Name.FieldPlaceholder')}
                rules={{ ...ValidationFormRules().requiredRule, validate: nameValidationRule(index) }}
              />
              <div className={styles.flex}>
                <HookFormTextInput
                  control={form.control}
                  name={`scheme.${index}.shortName`}
                  labelProps={{ text: t('ShortName.FieldLabel') }}
                  placeholder={t('ShortName.FieldPlaceholder')}
                  rules={ValidationFormRules().requiredRule}
                />
                <HookFormTextInput
                  control={form.control}
                  type={'number'}
                  min={1}
                  name={`scheme.${index}.limitation.numberOfEditingPeriod`}
                  labelProps={{
                    text: t('EditingPeriod.FieldLabel'),
                    tooltip: t('EditingPeriod.Tooltip'),
                  }}
                  placeholder={t('EditingPeriod.FieldPlaceholder')}
                  rules={ValidationFormRules().requiredRule}
                />
              </div>
              <HookFormCheckboxField
                control={form.control}
                name={`scheme.${index}.isFinalStep`}
                label={t('IsFinalStep')}
              />
              {availableFormOptions && (
                <Controller
                  control={form.control}
                  name={`scheme.${index}.forms`}
                  render={({ field: { onChange, value } }) => (
                    <AppDropdownField
                      isMultiple
                      labelProps={{
                        text: t('Forms.FieldLabel'),
                        rightSection: (
                          <Link to={navigateToRoute.studySettings(studyId)} target={'_blank'} className={styles.link}>
                            {t('CreateForm')}
                          </Link>
                        ),
                      }}
                      placeholder={t('Forms.FieldPlaceholder')}
                      options={availableFormOptions}
                      value={availableFormOptions.filter((x) => value.map((v) => v.formConfigId).includes(+x.key))}
                      onChange={(v) =>
                        onChange(
                          v?.map(
                            (x) =>
                              new CheckPointForm({
                                formConfigId: +x.key,
                                formConfigIdForInitialAnswers: null,
                              }),
                          ),
                        )
                      }
                    />
                  )}
                />
              )}

              <Controller
                control={form.control}
                name={`scheme.${index}.initState`}
                rules={ValidationFormRules().requiredRule}
                render={({ field: { onChange, value } }) => (
                  <AppDropdownField
                    isRequired
                    labelProps={{ text: t('InitialState.FieldLabel') }}
                    placeholder={t('InitialState.FieldPlaceholder')}
                    options={initStateOptions}
                    value={initStateOptions.find((o) => o.key === RouteProgressStateEnum[value])}
                    onChange={(v) => v?.key && onChange(RouteProgressStateEnum[v.key])}
                  />
                )}
              />
              {(scheme[index].initState === RouteProgressStateEnum.Init ||
                scheme[index].initState === RouteProgressStateEnum.None) && (
                <div className={styles.initStepContent}>
                  {scheme[index].initState === RouteProgressStateEnum.None && (
                    <div>
                      <AppInputLabel isBold text={t('StepStateTransferToInitStateRules.Header')}>
                        <Controller
                          control={form.control}
                          name={`scheme.${index}.stateTransferObject.formConfigId`}
                          rules={ValidationFormRules().requiredRule}
                          render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <AppDropdownField
                              isRequired
                              errorProps={{ errors: error?.message }}
                              labelProps={{
                                text: t('StepStateTransferToInProgressStateRules.FormWithInitialDataKey.FieldLabel'),
                                tooltip: t('StepStateTransferToInProgressStateRules.FormWithInitialDataKey.Tooltip'),
                              }}
                              placeholder={t(
                                'StepStateTransferToInProgressStateRules.FormWithInitialDataKey.FieldPlaceholder',
                              )}
                              options={availableFormOptions}
                              value={availableFormOptions.find((x) => x.key === value)}
                              onChange={(v) => onChange(v?.key)}
                            />
                          )}
                        />
                        <HookFormTextInput
                          control={form.control}
                          name={`scheme.${index}.stateTransferObject.dataKey`}
                          labelProps={{
                            text: t('StepStateTransferToInProgressStateRules.SelectedField.FieldLabel'),
                            tooltip: t('StepStateTransferToInProgressStateRules.SelectedField.Tooltip'),
                          }}
                          placeholder={t('StepStateTransferToInProgressStateRules.SelectedField.FieldPlaceholder')}
                          rules={ValidationFormRules().requiredRule}
                        />
                        <HookFormTextInput
                          control={form.control}
                          name={`scheme.${index}.stateTransferObject.value`}
                          labelProps={{
                            text: t('StepStateTransferToInProgressStateRules.SelectedFieldValue.FieldLabel'),
                            tooltip: t('StepStateTransferToInProgressStateRules.SelectedFieldValue.Tooltip'),
                          }}
                          placeholder={t('StepStateTransferToInProgressStateRules.SelectedFieldValue.FieldPlaceholder')}
                          rules={ValidationFormRules().requiredRule}
                        />
                      </AppInputLabel>
                    </div>
                  )}
                  <AppInputLabel isBold text={t('StepStateTransferToInProgressStateRules.Header')}>
                    <Controller
                      control={form.control}
                      name={`toInitEvent.${index}`}
                      rules={ValidationFormRules().requiredRule}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <AppRadioGroupField
                          labelProps={{
                            text: t('StepStateTransferToInProgressStateRules.EventSelector.FieldLabel'),
                            tooltip: t('StepStateTransferToInProgressStateRules.EventSelector.Tooltip'),
                          }}
                          labelField={'text'}
                          valueField={'key'}
                          errorProps={{ errors: error?.message }}
                          value={value}
                          onChange={(v) => {
                            if (v.key === 'DateField') {
                              form.setValue(`scheme.${index}.limitation.startWhenStepWithNameDone`, null);
                            }

                            if (v.key === 'StepDone') {
                              form.setValue(`scheme.${index}.limitation.numberOfStartShiftPeriod`, 0);
                              form.setValue(
                                `scheme.${index}.limitation.startDateActionObject`,
                                new FormActionObject({
                                  dataKey: null,
                                  formConfigId: null,
                                  funcName: null,
                                  value: null,
                                }),
                              );
                            }

                            onChange(v?.key);
                          }}
                          options={transferToInitEventOptions}
                        />
                      )}
                    />
                    {form.watch(`toInitEvent.${index}`) === 'StepDone' && (
                      <Controller
                        control={form.control}
                        name={`scheme.${index}.limitation.startWhenStepWithNameDone`}
                        rules={{ ...ValidationFormRules().requiredRule, validate: selectStepNameValidationRule(index) }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <AppDropdownField
                            isRequired
                            options={stepNameOptions}
                            errorProps={{ errors: error?.message }}
                            labelProps={{
                              text: t('StepStateTransferToInitStateRules.StartWhenStepWithNameDone.FieldLabel'),
                              tooltip: t('StepStateTransferToInitStateRules.StartWhenStepWithNameDone.Tooltip'),
                            }}
                            placeholder={t(
                              'StepStateTransferToInitStateRules.StartWhenStepWithNameDone.FieldPlaceholder',
                            )}
                            value={stepNameOptions.find((x) => x.key === value)}
                            onChange={(x) => onChange(x?.key)}
                          />
                        )}
                      />
                    )}
                    {form.watch(`toInitEvent.${index}`) === 'DateField' && (
                      <>
                        <Controller
                          control={form.control}
                          name={`scheme.${index}.limitation.startDateActionObject.formConfigId`}
                          rules={ValidationFormRules().requiredRule}
                          render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <AppDropdownField
                              isRequired
                              errorProps={{ errors: error?.message }}
                              labelProps={{
                                text: t('FormWithInitialDataKey.FieldLabel'),
                                tooltip: t('FormWithInitialDataKey.Tooltip'),
                              }}
                              placeholder={t('FormWithInitialDataKey.FieldPlaceholder')}
                              options={availableFormOptions}
                              value={availableFormOptions.find((x) => x.key === value)}
                              onChange={(v) => onChange(v?.key)}
                            />
                          )}
                        />
                        <HookFormTextInput
                          control={form.control}
                          name={`scheme.${index}.limitation.startDateActionObject.dataKey`}
                          labelProps={{
                            text: t('InitialFieldDataKey.FieldLabel'),
                            tooltip: t('InitialFieldDataKey.Tooltip'),
                          }}
                          placeholder={t('InitialFieldDataKey.FieldPlaceholder')}
                          rules={ValidationFormRules().requiredRule}
                        />
                        <HookFormTextInput
                          control={form.control}
                          type={'number'}
                          name={`scheme.${index}.limitation.numberOfStartShiftPeriod`}
                          labelProps={{
                            text: t('StartShiftingPeriod.FieldLabel'),
                            tooltip: t('StartShiftingPeriod.Tooltip'),
                          }}
                          placeholder={t('StartShiftingPeriod.FieldPlaceholder')}
                          rules={ValidationFormRules().requiredRule}
                        />
                      </>
                    )}
                  </AppInputLabel>
                </div>
              )}

              <AppButton
                variant={'icon-link'}
                colorSchema={'decline'}
                text={t('RemoveStep')}
                onClick={() => {
                  checkpointArray.remove(index);
                  setCurrentStep(index);
                }}
                className={styles.removeButton}
              />
            </div>
          ))}
        </div>

        <AppButton
          variant={'icon-link'}
          colorSchema={'primary'}
          Icon={AddIcon}
          className={styles.addButton}
          text={t('AddNewStep')}
          onClick={() => {
            checkpointArray.append(
              new CheckPoint({
                name: 'First step',
                shortName: '1st.',
                forms: [],
                initState: RouteProgressStateEnum.InProgress,
                stateTransferObject: new FormActionObject({
                  dataKey: null,
                  formConfigId: null,
                  funcName: null,
                  value: null,
                }),
                isFinalStep: false,
                limitation: new StepTimeLimitation({
                  // TODO: Change to enum
                  editingPeriodUnits: 'D',
                  numberOfEditingPeriod: 49,
                  // TODO: Change to enum
                  startPeriodUnits: 'D',
                  numberOfStartShiftPeriod: 0,
                  startDateActionObject: new FormActionObject({
                    dataKey: null,
                    formConfigId: null,
                    funcName: null,
                    value: null,
                  }),
                  startWhenStepWithNameDone: null,
                }),
              }),
            );
            setCurrentStep(checkpointArray.fields.length + 1);
          }}
        />
      </div>
    </DialogModal>
  );
};
